import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
  Box, Grid, Paper, Stack, Typography, Chip, Button, TextField, MenuItem, Divider,
  useMediaQuery, useTheme,
} from "@mui/material";
import GlobalWrapper from "../../../components/Wrapper/GlobalWrapper";
import GlobalDialog from "../../../components/mui/Dialogue/GlobalDialog";
import { AddButton } from "../../../components/mui/Buttons/Buttons";
import {
  getFormTemplates, createFormTemplate, updateFormTemplateStatus, addFormField, deleteFormField,
  updateFormField, updateFormTemplate,
} from "../../../redux/features/careerSlice";

const FIELD_TYPES = ["text", "textarea", "number", "date", "select", "multiselect", "url", "boolean"];

// the key is internal-only (officers never see it), so keep it short and tidy
const KEY_MAX = 30;

// Turn a human label into a machine key: "Portfolio URL!" -> "portfolio_url".
// (lowercase, spaces/symbols become underscores, no leading/trailing underscore)
// If the label is very long, cap the key and cut back to the last whole word.
const toKey = (s) => {
  let k = s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  if (k.length > KEY_MAX) {
    k = k.slice(0, KEY_MAX);
    const lastUnderscore = k.lastIndexOf("_");
    // trim a half-cut trailing word back to the last full word (unless that empties the key)
    if (lastUnderscore > 0) k = k.slice(0, lastUnderscore);
    k = k.replace(/_+$/, "");
  }
  return k;
};
const TEMPLATE_STATUS = { 0: "Inactive", 1: "Active", 2: "Deleted" };

// Form builder: officers define reusable post-job form templates + their fields.
const CareerFormTemplates = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { formTemplates, formLoading } = useSelector((state) => state.career);

  // on phones every dialog goes full screen so the builder isn't cramped
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md")); // phones + small tablets

  // true while ANY save/delete API call is in flight — disables action buttons
  // so a double-click can't fire the same action twice
  const [busy, setBusy] = useState(false);

  const [newOpen, setNewOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  const [tplDesc, setTplDesc] = useState("");
  // when set, the New/Edit Template dialog is EDITING this template (not creating)
  const [editingTemplateId, setEditingTemplateId] = useState(null);

  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState(null);

  // read-only preview: shows how the template's fields look on the job form
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const openPreview = (tpl) => { setPreviewTemplate(tpl); setPreviewOpen(true); };

  // new-field form state
  const [fLabel, setFLabel] = useState("");
  // key is auto-generated from the label and shown read-only (officers never edit it)
  const [fKey, setFKey] = useState("");
  const [fType, setFType] = useState("text");
  const [fRequired, setFRequired] = useState(false);
  const [fOptions, setFOptions] = useState("");
  // when set, the form is EDITING this existing field (instead of adding a new one)
  const [editingFieldId, setEditingFieldId] = useState(null);

  const load = () => dispatch(getFormTemplates());
  useEffect(() => { load(); }, []); // eslint-disable-line

  // reload templates and re-select the active one (so its field list refreshes)
  const refreshActive = () =>
    dispatch(getFormTemplates()).then((res) => {
      const updated = (res.payload?.data || []).find(
        (t) => t.career_form_template_id === activeTemplate?.career_form_template_id
      );
      if (updated) setActiveTemplate(updated);
    });

  // open the dialog to create a new template
  const openNewTemplate = () => {
    setEditingTemplateId(null); setTplName(""); setTplDesc(""); setNewOpen(true);
  };

  // open the dialog to edit an existing template's name/description
  const openEditTemplate = (tpl) => {
    setEditingTemplateId(tpl.career_form_template_id);
    setTplName(tpl.template_name || "");
    setTplDesc(tpl.description || "");
    setNewOpen(true);
  };

  // create a new template OR save name/description edits
  const saveTemplate = () => {
    if (!tplName.trim()) return enqueueSnackbar("Template name required", { variant: "warning" });
    const body = { template_name: tplName, description: tplDesc };
    const action = editingTemplateId
      ? updateFormTemplate({ templateId: editingTemplateId, ...body })
      : createFormTemplate(body);
    setBusy(true);
    dispatch(action)
      .then((res) => {
        if (res.payload?.status === "success") {
          enqueueSnackbar(editingTemplateId ? "Template updated" : "Template created", { variant: "success" });
          setTplName(""); setTplDesc(""); setEditingTemplateId(null); setNewOpen(false); load();
        } else enqueueSnackbar(res.payload?.message || "Failed", { variant: "error" });
      })
      .finally(() => setBusy(false));
  };

  const setDefault = (tpl) => {
    setBusy(true);
    dispatch(updateFormTemplateStatus({ templateId: tpl.career_form_template_id, is_default: true }))
      .then(() => { enqueueSnackbar("Default template set", { variant: "success" }); load(); })
      .finally(() => setBusy(false));
  };

  const deleteTemplate = (tpl) => {
    setBusy(true);
    dispatch(updateFormTemplateStatus({ templateId: tpl.career_form_template_id, template_status: 2 }))
      .then(() => { enqueueSnackbar("Template deleted", { variant: "success" }); load(); })
      .finally(() => setBusy(false));
  };

  const openFields = (tpl) => { setActiveTemplate(tpl); resetFieldForm(); setFieldsOpen(true); };

  // clear the field form back to "add a new field" mode
  const resetFieldForm = () => {
    setFLabel(""); setFKey(""); setFType("text"); setFRequired(false); setFOptions(""); setEditingFieldId(null);
  };

  // load an existing field into the form to edit it
  const startEditField = (f) => {
    setEditingFieldId(f.career_form_field_id);
    setFLabel(f.field_label || "");
    setFKey(f.field_key || ""); // key stays fixed while editing (keeps saved job answers valid)
    setFType(f.field_type || "text");
    setFRequired(!!f.is_required);
    setFOptions(Array.isArray(f.field_options) ? f.field_options.join(", ") : "");
  };

  // shared payload for add + edit (options only kept for choice types)
  const buildFieldData = () => {
    const data = { field_label: fLabel, field_type: fType, is_required: fRequired ? 1 : 0 };
    data.field_options =
      fType === "select" || fType === "multiselect"
        ? fOptions.split(",").map((s) => s.trim()).filter(Boolean)
        : null; // clear options when the type isn't a dropdown
    return data;
  };

  // add a new field OR save edits to the one being edited
  const saveField = () => {
    if (!fLabel.trim()) return enqueueSnackbar("Field label required", { variant: "warning" });
    const data = buildFieldData();
    const action = editingFieldId
      // EDIT: send the key too so it stays in sync with the (possibly changed) label
      ? updateFormField({ fieldId: editingFieldId, field_key: fKey, ...data })
      : addFormField({ templateId: activeTemplate.career_form_template_id, field_key: fKey, ...data });
    if (!editingFieldId && !fKey.trim()) return enqueueSnackbar("Field label required", { variant: "warning" });

    setBusy(true);
    dispatch(action)
      .then((res) => {
        if (res.payload?.status === "success") {
          enqueueSnackbar(editingFieldId ? "Field updated" : "Field added", { variant: "success" });
          resetFieldForm();
          refreshActive();
        } else enqueueSnackbar(res.payload?.message || "Failed", { variant: "error" });
      })
      .finally(() => setBusy(false));
  };

  const removeField = (fieldId) => {
    setBusy(true);
    dispatch(deleteFormField(fieldId))
      .then(() => {
        enqueueSnackbar("Field removed", { variant: "success" });
        if (fieldId === editingFieldId) resetFieldForm(); // if we deleted the one being edited
        refreshActive();
      })
      .finally(() => setBusy(false));
  };

  // render one field exactly how it looks on the job form — disabled (view only)
  const renderPreviewField = (f) => {
    const label = f.field_label + (f.is_required ? " *" : "");
    const opts = Array.isArray(f.field_options) ? f.field_options : [];
    const common = { fullWidth: true, size: "small", label, placeholder: f.placeholder || "", disabled: true };
    switch (f.field_type) {
      case "textarea":
        return <TextField {...common} multiline minRows={2} />;
      case "number":
        return <TextField {...common} type="number" />;
      case "date":
        return <TextField {...common} type="date" InputLabelProps={{ shrink: true }} />;
      case "url":
        return <TextField {...common} type="url" />;
      case "boolean":
        return (
          <TextField {...common} select value="">
            <MenuItem value="">Yes / No</MenuItem>
          </TextField>
        );
      case "select":
      case "multiselect":
        return (
          <TextField
            {...common}
            select
            value=""
            helperText={opts.length ? `Choices: ${opts.join(", ")}` : "No choices set"}
          >
            <MenuItem value="">{f.field_type === "multiselect" ? "Choose one or more…" : "Choose…"}</MenuItem>
          </TextField>
        );
      default: // text
        return <TextField {...common} />;
    }
  };

  const fields = activeTemplate?.fields || [];

  return (
    <GlobalWrapper
      title={"Form Templates"}
      actions={<AddButton size="medium" action={openNewTemplate}>New Template</AddButton>}
    >
      <Grid container spacing={2}>
        {formLoading && !formTemplates.length && (
          <Grid item xs={12}><Typography color="text.secondary">Loading…</Typography></Grid>
        )}
        {!formLoading && formTemplates.length === 0 && (
          <Grid item xs={12}>
            <Typography color="text.secondary">
              No form templates yet. Create one to customise the post-job form.
            </Typography>
          </Grid>
        )}
        {formTemplates.map((tpl) => (
          <Grid item xs={12} md={6} lg={4} key={tpl.career_form_template_id}>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    onClick={() => openPreview(tpl)}
                    sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                  >
                    {tpl.template_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{tpl.description || ""}</Typography>
                </Box>
                {tpl.is_default ? <Chip size="small" color="primary" label="Default" /> : null}
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", gap: 1 }}>
                <Chip size="small" variant="outlined" label={`${(tpl.fields || []).length} fields`} />
                <Chip size="small" variant="outlined" label={TEMPLATE_STATUS[tpl.template_status]} />
              </Stack>
              <Divider sx={{ my: 1.5 }} />
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                <Button size="small" variant="outlined" onClick={() => openPreview(tpl)}>Preview</Button>
                <Button size="small" variant="outlined" onClick={() => openEditTemplate(tpl)}>Edit</Button>
                <Button size="small" variant="outlined" onClick={() => openFields(tpl)}>Manage Fields</Button>
                {!tpl.is_default && <Button size="small" onClick={() => setDefault(tpl)} disabled={busy}>{busy ? "Working…" : "Set Default"}</Button>}
                <Button size="small" color="error" onClick={() => deleteTemplate(tpl)} disabled={busy}>{busy ? "Working…" : "Delete"}</Button>
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* New / Edit Template */}
      <GlobalDialog
        title={editingTemplateId ? "Edit Form Template" : "New Form Template"}
        open={newOpen} setOpen={setNewOpen} fullWidth fullScreen={fullScreen} maxWidth="sm" closeButton
        content={
          <Stack spacing={2}>
            <TextField label="Template Name *" value={tplName} onChange={(e) => setTplName(e.target.value)} fullWidth />
            <TextField label="Description" value={tplDesc} onChange={(e) => setTplDesc(e.target.value)} fullWidth />
            <Box sx={{ display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: 1 }}>
              <Button variant="outlined" onClick={() => setNewOpen(false)} disabled={busy}>Cancel</Button>
              <Button variant="contained" onClick={saveTemplate} disabled={busy}>
                {busy ? "Saving…" : editingTemplateId ? "Save Changes" : "Create"}
              </Button>
            </Box>
          </Stack>
        }
      />

      {/* Manage Fields */}
      <GlobalDialog
        title={`Fields: ${activeTemplate?.template_name || ""}`}
        open={fieldsOpen} setOpen={setFieldsOpen} fullWidth fullScreen={fullScreen} maxWidth="md" closeButton
        content={
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Current fields</Typography>
            {fields.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No fields yet.</Typography>
            ) : (
              <Stack spacing={1} sx={{ mb: 2 }}>
                {fields.map((f) => (
                  <Paper key={f.career_form_field_id} variant="outlined"
                    sx={{ p: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center",
                      flexWrap: "wrap", gap: 1,
                      ...(f.career_form_field_id === editingFieldId ? { borderColor: "primary.main" } : {}) }}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {f.field_label}
                        {f.is_required ? <Chip size="small" label="required" sx={{ ml: 1 }} /> : null}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {f.field_type} · key: {f.field_key}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                      <Button size="small" variant="outlined" onClick={() => startEditField(f)} disabled={busy}>Edit</Button>
                      <Button size="small" color="error" onClick={() => removeField(f.career_form_field_id)} disabled={busy}>
                        {busy ? "Working…" : "Remove"}
                      </Button>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {editingFieldId ? "Edit field" : "Add a field"}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Label *"
                  placeholder="e.g. Portfolio URL"
                  size="small"
                  fullWidth
                  value={fLabel}
                  inputProps={{ maxLength: 150 }}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFLabel(v);
                    setFKey(toKey(v)); // key always follows the label (both add and edit)
                  }}
                  helperText="What people see on the job form"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Key (auto)"
                  size="small"
                  fullWidth
                  value={fKey}
                  InputProps={{ readOnly: true }}
                  helperText="Internal name, generated automatically, no need to edit"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select label="Type" size="small" fullWidth value={fType} onChange={(e) => setFType(e.target.value)}>
                  {FIELD_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select label="Required" size="small" fullWidth value={fRequired ? "yes" : "no"} onChange={(e) => setFRequired(e.target.value === "yes")}>
                  <MenuItem value="no">No</MenuItem>
                  <MenuItem value="yes">Yes</MenuItem>
                </TextField>
              </Grid>
              {(fType === "select" || fType === "multiselect") && (
                <Grid item xs={12} sm={6}>
                  <TextField label="Options (comma separated)" size="small" fullWidth value={fOptions} onChange={(e) => setFOptions(e.target.value)} />
                </Grid>
              )}
            </Grid>
            <Box sx={{ display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: 1, mt: 2 }}>
              {editingFieldId && (
                <Button variant="outlined" onClick={resetFieldForm} disabled={busy}>Cancel edit</Button>
              )}
              <Button variant="contained" onClick={saveField} disabled={busy}>
                {busy ? "Saving…" : editingFieldId ? "Update Field" : "Add Field"}
              </Button>
            </Box>
          </Box>
        }
      />

      {/* Preview — read-only look of the form the officer will see on a job */}
      <GlobalDialog
        title={`Preview: ${previewTemplate?.template_name || ""}`}
        open={previewOpen} setOpen={setPreviewOpen} fullWidth fullScreen={fullScreen} maxWidth="sm" closeButton
        content={
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This is how the extra fields appear on the Post Job form. (View only. You can't type here.)
            </Typography>
            {(previewTemplate?.fields || []).length === 0 ? (
              <Typography color="text.secondary">This template has no fields yet.</Typography>
            ) : (
              <Stack direction="column" spacing={2}>
                {(previewTemplate?.fields || []).map((f) => (
                  <Box key={f.career_form_field_id}>{renderPreviewField(f)}</Box>
                ))}
              </Stack>
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button variant="outlined" onClick={() => setPreviewOpen(false)}>Close</Button>
            </Box>
          </Box>
        }
      />
    </GlobalWrapper>
  );
};

export default CareerFormTemplates;
